import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import i18n from '@/lib/i18n';
import { setAccessToken, setRefreshFunction, apiClient } from '@/api/client';
import type { AuthResponse, User } from '@/api/types';

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-fetches the current user from {@code GET /users/me} and
   * replaces the in-context user. Used after side-channel updates
   * (e.g. phone verification flow) so {@code phoneVerified} flips
   * without forcing a logout/login round trip.
   */
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

const REFRESH_TOKEN_KEY = 'recplay_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleAuthResponse = useCallback((data: AuthResponse) => {
    setAccessToken(data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    setUser(data.user);
  }, []);

  const clearAuth = useCallback(() => {
    setAccessToken(null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return null;

    try {
      const data = await apiClient<AuthResponse>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
        skipAuth: true,
      });
      handleAuthResponse(data);
      return data.accessToken;
    } catch {
      clearAuth();
      return null;
    }
  }, [handleAuthResponse, clearAuth]);

  // Wire refresh function into apiClient for automatic 401 retry
  useEffect(() => {
    setRefreshFunction(refresh);
  }, [refresh]);

  // Sync i18next to the user's profile-stored locale whenever the user
  // resolves (login, silent refresh on mount, refreshUser after a side-
  // channel update). The profile is the canonical store for authenticated
  // users; anonymous users continue to use LanguageDetector's localStorage
  // path (see lib/i18n.ts). Profile stores 'EN' / 'FR'; i18next uses
  // 'en' / 'fr'. Skip the call when already in sync to avoid unnecessary
  // re-renders. See doc/DECISIONS.md "Language Switcher Single Source of
  // Truth" for the full design.
  useEffect(() => {
    if (!user?.locale) return;
    const targetLang = user.locale.toLowerCase();
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [user?.locale]);

  // On mount: attempt silent refresh if we have a stored refresh token
  useEffect(() => {
    const storedToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedToken) {
      refresh().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const data = await apiClient<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        skipAuth: true,
      });
      handleAuthResponse(data);
      return data.user;
    },
    [handleAuthResponse]
  );

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      phone?: string;
    }) => {
      const authData = await apiClient<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      });
      handleAuthResponse(authData);
    },
    [handleAuthResponse]
  );

  const refreshUser = useCallback(async () => {
    try {
      const fresh = await apiClient<User>('/users/me');
      setUser(fresh);
    } catch {
      // Swallow — caller can react to stale state via re-fetch.
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      try {
        await apiClient<void>('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Logout should succeed even if API call fails
      }
    }
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
